import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  TrendUp, 
  TrendDown,
  Target,
  ShieldWarning,
  Lightning,
  Clock,
  ChartLineUp,
  Info,
  Check,
  X,
  CandlestickChart,
  CurrencyCircleDollar
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';

const ScoreRing = ({ score }) => {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  let color = "#64748b";
  if (score >= 90) color = "#16a34a";
  else if (score >= 85) color = "#2563eb";
  else if (score >= 70) color = "#d97706";
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="120" height="120" className="-rotate-90">
        <circle cx="60" cy="60" r={radius} stroke="#e2e8f0" strokeWidth="8" fill="none" />
        <circle cx="60" cy="60" r={radius} stroke={color} strokeWidth="8" fill="none"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="score-ring" />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-data text-3xl font-bold text-slate-900 tabular-nums">{score}</span>
        <span className="text-xs text-slate-500 font-body uppercase">Score</span>
      </div>
    </div>
  );
};

const TIMEFRAMES = [
  { key: "1m", label: "1m", days: 1 },
  { key: "5m", label: "5m", days: 1 },
  { key: "15m", label: "15m", days: 1 },
  { key: "30m", label: "30m", days: 1 },
  { key: "1H", label: "1H", days: 7 },
  { key: "2H", label: "2H", days: 14 },
  { key: "4H", label: "4H", days: 30 },
  { key: "1D", label: "1J", days: 30 },
];

const CandlestickChartComponent = ({ symbol }) => {
  const chartContainerRef = useRef(null);
  const [ohlcData, setOhlcData] = useState([]);
  const [overlays, setOverlays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMA, setShowMA] = useState(true);
  const [showBollinger, setShowBollinger] = useState(false);
  const [showVWAP, setShowVWAP] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("1D");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const tf = TIMEFRAMES.find(t => t.key === selectedTimeframe) || TIMEFRAMES[7];
        const [ohlcRes, overlayRes] = await Promise.all([
          fetch(`${API}/ohlc/${symbol}?days=${tf.days}&timeframe=${selectedTimeframe}`),
          fetch(`${API}/ohlc/${symbol}/overlays?days=${tf.days}&timeframe=${selectedTimeframe}`)
        ]);
        if (ohlcRes.ok) setOhlcData(await ohlcRes.json());
        if (overlayRes.ok) setOverlays(await overlayRes.json());
      } catch (e) {
        console.error("Error fetching OHLC:", e);
        setError("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [symbol, selectedTimeframe]);

  useEffect(() => {
    if (!chartContainerRef.current || ohlcData.length === 0) return;
    
    let chart = null;
    let mounted = true;
    
    const timer = setTimeout(() => {
      if (!mounted || !chartContainerRef.current) return;
      
      const containerWidth = chartContainerRef.current.clientWidth;
      if (containerWidth <= 0) return;
      
      const isIntraday = selectedTimeframe !== "1D";
      
      try {
        chart = createChart(chartContainerRef.current, {
          width: containerWidth,
          height: 350,
          layout: { background: { color: '#ffffff' }, textColor: '#64748b', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 },
          grid: { vertLines: { color: '#f1f5f9' }, horzLines: { color: '#f1f5f9' } },
          crosshair: { mode: 0 },
          rightPriceScale: { borderColor: '#e2e8f0' },
          timeScale: { borderColor: '#e2e8f0', timeVisible: isIntraday, secondsVisible: false },
          localization: { locale: 'fr-FR' },
        });

        const candleSeries = chart.addSeries(CandlestickSeries, {
          upColor: '#16a34a', downColor: '#dc2626',
          borderUpColor: '#16a34a', borderDownColor: '#dc2626',
          wickUpColor: '#16a34a', wickDownColor: '#dc2626',
        });
        candleSeries.setData(ohlcData);

        if (overlays) {
          if (showMA && overlays.ma20?.length > 0) {
            const ma20Series = chart.addSeries(LineSeries, { color: '#f59e0b', lineWidth: 1, title: 'MA20' });
            ma20Series.setData(overlays.ma20);
          }
          if (showMA && overlays.ma50?.length > 0) {
            const ma50Series = chart.addSeries(LineSeries, { color: '#8b5cf6', lineWidth: 1, title: 'MA50' });
            ma50Series.setData(overlays.ma50);
          }
          if (showBollinger && overlays.bollinger_upper?.length > 0) {
            const bbUpper = chart.addSeries(LineSeries, { color: '#ef4444', lineWidth: 1, lineStyle: 2, title: 'BB+' });
            bbUpper.setData(overlays.bollinger_upper);
            const bbLower = chart.addSeries(LineSeries, { color: '#22c55e', lineWidth: 1, lineStyle: 2, title: 'BB-' });
            bbLower.setData(overlays.bollinger_lower);
            const bbMid = chart.addSeries(LineSeries, { color: '#94a3b8', lineWidth: 1, lineStyle: 1, title: 'BB' });
            bbMid.setData(overlays.bollinger_middle);
          }
          if (showVWAP && overlays.vwap?.length > 0) {
            const vwapSeries = chart.addSeries(LineSeries, { color: '#0ea5e9', lineWidth: 2, title: 'VWAP' });
            vwapSeries.setData(overlays.vwap);
          }
        }

        chart.timeScale().fitContent();
      } catch (e) {
        console.error("Error creating chart:", e);
        setError("Erreur d'affichage du graphique");
      }
    }, 100);

    const handleResize = () => {
      if (chartContainerRef.current && chart) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      mounted = false;
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (chart) chart.remove();
    };
  }, [ohlcData, overlays, showMA, showBollinger, showVWAP, selectedTimeframe]);

  if (loading) return <Skeleton className="h-[350px] w-full" />;
  if (error) return <p className="text-sm text-red-400 text-center py-8">{error}</p>;
  if (ohlcData.length === 0) return <p className="text-sm text-slate-400 text-center py-8">Donnees chandeliers non disponibles</p>;

  return (
    <div>
      {/* Timeframe selector */}
      <div className="flex gap-1 mb-2 flex-wrap" data-testid="timeframe-selector">
        {TIMEFRAMES.map(tf => (
          <Button
            key={tf.key}
            variant={selectedTimeframe === tf.key ? "default" : "outline"}
            size="sm"
            className={`rounded-sm text-xs h-6 px-2 ${selectedTimeframe === tf.key ? 'bg-slate-900' : ''}`}
            onClick={() => setSelectedTimeframe(tf.key)}
            data-testid={`tf-${tf.key}`}
          >
            {tf.label}
          </Button>
        ))}
      </div>
      {/* Overlay toggles */}
      <div className="flex gap-2 mb-2" data-testid="overlay-toggles">
        <Button variant={showMA ? "default" : "outline"} size="sm" className="rounded-sm text-xs h-6 px-2" onClick={() => setShowMA(!showMA)} data-testid="toggle-ma">
          MA
        </Button>
        <Button variant={showBollinger ? "default" : "outline"} size="sm" className="rounded-sm text-xs h-6 px-2" onClick={() => setShowBollinger(!showBollinger)} data-testid="toggle-bollinger">
          Bollinger
        </Button>
        <Button variant={showVWAP ? "default" : "outline"} size="sm" className="rounded-sm text-xs h-6 px-2" onClick={() => setShowVWAP(!showVWAP)} data-testid="toggle-vwap">
          VWAP
        </Button>
        <div className="flex items-center gap-2 ml-2 text-[10px] text-slate-400">
          {showMA && <><span className="w-2 h-0.5 bg-amber-500 inline-block"></span>MA20 <span className="w-2 h-0.5 bg-purple-500 inline-block"></span>MA50</>}
          {showBollinger && <><span className="w-2 h-0.5 bg-red-500 inline-block"></span>BB+ <span className="w-2 h-0.5 bg-green-500 inline-block"></span>BB-</>}
          {showVWAP && <><span className="w-2 h-0.5 bg-sky-500 inline-block"></span>VWAP</>}
        </div>
      </div>
      <div ref={chartContainerRef} data-testid="candlestick-chart" className="w-full" style={{minHeight: '350px'}} />
    </div>
  );
};

const PriceChart = ({ data, support, resistance }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.map((item, index) => ({ index, price: item.price }));
  const minPrice = Math.min(...data.map(d => d.price)) * 0.99;
  const maxPrice = Math.max(...data.map(d => d.price)) * 1.01;
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <XAxis dataKey="index" hide />
          <YAxis domain={[minPrice, maxPrice]} hide />
          <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '12px', fontFamily: 'JetBrains Mono' }}
            formatter={(value) => [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`, 'Prix']} />
          {support && <ReferenceLine y={support} stroke="#16a34a" strokeDasharray="3 3" label={{ value: 'Support', position: 'left', fontSize: 10, fill: '#16a34a' }} />}
          {resistance && <ReferenceLine y={resistance} stroke="#dc2626" strokeDasharray="3 3" label={{ value: 'Resistance', position: 'left', fontSize: 10, fill: '#dc2626' }} />}
          <Line type="linear" dataKey="price" stroke="#2563eb" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const ScoreCriteria = ({ criteria }) => {
  const items = [
    { key: 'trend_aligned', label: 'Tendance alignee', max: 20 },
    { key: 'volume_strong', label: 'Volume fort', max: 15 },
    { key: 'support_zone', label: 'Zone support/resistance', max: 20 },
    { key: 'pattern_confirmed', label: 'Pattern confirme', max: 15 },
    { key: 'liquidity_valid', label: 'Liquidite validee', max: 20 },
    { key: 'timing_precise', label: 'Timing precis', max: 10 },
  ];
  return (
    <div className="space-y-3">
      {items.map(({ key, label, max }) => {
        const value = criteria?.[key] || 0;
        const percentage = (value / max) * 100;
        return (
          <div key={key}>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-slate-600 font-body">{label}</span>
              <span className="font-data text-sm text-slate-900 tabular-nums">{value}/{max}</span>
            </div>
            <Progress value={percentage} className="h-1.5" />
          </div>
        );
      })}
    </div>
  );
};

const TechnicalIndicators = ({ indicators }) => {
  if (!indicators) return null;
  const getRSIColor = (rsi) => rsi < 30 ? "text-emerald-600" : rsi > 70 ? "text-red-600" : "text-slate-600";
  const getStochColor = (stoch) => stoch < 20 ? "text-emerald-600" : stoch > 80 ? "text-red-600" : "text-slate-600";
  const isSwingMode = indicators.analysis_mode === "swing";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Badge className={isSwingMode ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
          {isSwingMode ? "SWING TRADING" : "DAY TRADING"}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">RSI</p>
          <p className={`font-data text-lg font-medium tabular-nums ${getRSIColor(indicators.rsi)}`}>{indicators.rsi?.toFixed(1)}</p>
          <p className="text-xs text-slate-400">{indicators.rsi < 30 ? 'Survendu' : indicators.rsi > 70 ? 'Surachete' : 'Neutre'}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">MACD</p>
          <p className={`font-data text-lg font-medium tabular-nums ${indicators.macd > indicators.macd_signal ? 'text-emerald-600' : 'text-red-600'}`}>
            {indicators.macd?.toFixed(4)}
          </p>
          <p className="text-xs text-slate-400">Signal: {indicators.macd_signal?.toFixed(4)}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">MA 20 / MA 50</p>
          <p className="font-data text-sm tabular-nums text-slate-600">${indicators.ma_20?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          <p className="font-data text-sm tabular-nums text-slate-600">${indicators.ma_50?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">ATR</p>
          <p className="font-data text-lg font-medium tabular-nums text-slate-600">{indicators.atr?.toFixed(4)}</p>
          <p className="text-xs text-slate-400">Volatilite</p>
        </div>
        {indicators.vwap && (
          <div className="p-3 bg-slate-50 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">VWAP</p>
            <p className="font-data text-lg font-medium tabular-nums text-slate-600">${indicators.vwap?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
        )}
        {indicators.adx && (
          <div className="p-3 bg-slate-50 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">ADX</p>
            <p className={`font-data text-lg font-medium tabular-nums ${indicators.adx > 25 ? 'text-emerald-600' : 'text-slate-600'}`}>{indicators.adx?.toFixed(1)}</p>
            <p className="text-xs text-slate-400">{indicators.adx > 25 ? 'Tendance forte' : 'Pas de tendance'}</p>
          </div>
        )}
        {indicators.obv && (
          <div className="p-3 bg-slate-50 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">OBV</p>
            <p className={`font-data text-sm font-medium ${indicators.obv_signal === 'bullish' ? 'text-emerald-600' : indicators.obv_signal === 'bearish' ? 'text-red-600' : 'text-slate-600'}`}>
              {indicators.obv_signal === 'bullish' ? 'Haussier' : indicators.obv_signal === 'bearish' ? 'Baissier' : 'Neutre'}
            </p>
          </div>
        )}
        {indicators.stoch_rsi_k != null && (
          <div className="p-3 bg-slate-50 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Stoch RSI</p>
            <p className={`font-data text-lg font-medium tabular-nums ${getStochColor(indicators.stoch_rsi_k)}`}>
              K: {indicators.stoch_rsi_k?.toFixed(1)} / D: {indicators.stoch_rsi_d?.toFixed(1)}
            </p>
          </div>
        )}
      </div>
      {indicators.bollinger_upper && (
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Bollinger Bands</p>
          <div className="flex justify-between font-data text-sm tabular-nums">
            <span className="text-red-600">H: ${indicators.bollinger_upper?.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
            <span className="text-slate-600">M: ${indicators.bollinger_middle?.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
            <span className="text-emerald-600">B: ${indicators.bollinger_lower?.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
          </div>
        </div>
      )}
      {indicators.fib_382 && (
        <div className="p-3 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Fibonacci</p>
          <div className="grid grid-cols-4 gap-2 font-data text-xs tabular-nums text-slate-600">
            <div>23.6%: ${indicators.fib_236?.toFixed(2)}</div>
            <div>38.2%: ${indicators.fib_382?.toFixed(2)}</div>
            <div>50%: ${indicators.fib_500?.toFixed(2)}</div>
            <div>61.8%: ${indicators.fib_618?.toFixed(2)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function OpportunityDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [positionSize, setPositionSize] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [chartMode, setChartMode] = useState("line");
  const [paperTradeDialogOpen, setPaperTradeDialogOpen] = useState(false);
  const [paperPositionSize, setPaperPositionSize] = useState("");
  const [submittingPaper, setSubmittingPaper] = useState(false);
  const [trailingStopEnabled, setTrailingStopEnabled] = useState(false);
  const [trailingStopPercent, setTrailingStopPercent] = useState("5");
  const [paperTrailingEnabled, setPaperTrailingEnabled] = useState(false);
  const [paperTrailingPercent, setPaperTrailingPercent] = useState("5");

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const response = await fetch(`${API}/opportunities/${symbol}`);
        if (!response.ok) throw new Error("Not found");
        const data = await response.json();
        setOpportunity(data);
      } catch (error) {
        console.error("Error:", error);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [symbol]);

  // Live price refresh every 30s
  useEffect(() => {
    if (!symbol) return;
    const refreshPrice = async () => {
      try {
        const res = await fetch(`${API}/prices/live`);
        if (!res.ok) return;
        const livePrices = await res.json();
        const liveData = livePrices[symbol.toUpperCase()];
        if (liveData && liveData.price > 0) {
          setOpportunity(prev => prev ? { ...prev, current_price: liveData.price, price_change_24h: liveData.change_24h } : prev);
        }
      } catch (e) { /* silent */ }
    };
    const priceInterval = setInterval(refreshPrice, 30000);
    return () => clearInterval(priceInterval);
  }, [symbol]);

  const handleTradeClick = async () => {
    try {
      const response = await fetch(`${API}/trades?status=open`);
      const openTrades = await response.json();
      const existingTrade = openTrades.find(t => t.symbol.toUpperCase() === opportunity.symbol.toUpperCase());
      if (existingTrade) {
        setDuplicateDialogOpen(true);
        return;
      }
    } catch (e) { /* continue */ }
    setTradeDialogOpen(true);
  };

  const handleSubmitTrade = async () => {
    if (!positionSize || parseFloat(positionSize) <= 0) {
      toast.error("Entrez une taille de position valide");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/trades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunity_id: opportunity.id,
          symbol: opportunity.symbol,
          market_type: opportunity.market_type,
          entry_price: opportunity.current_price,
          stop_loss: opportunity.stop_loss,
          take_profit: opportunity.take_profit?.[0] || opportunity.current_price * 1.05,
          position_size: parseFloat(positionSize),
          score_at_entry: opportunity.score,
          notes: `Entry manuelle - Score ${opportunity.score}${trailingStopEnabled ? ` - Trailing ${trailingStopPercent}%` : ''}`,
          trailing_stop_percent: trailingStopEnabled ? parseFloat(trailingStopPercent) : null
        })
      });
      if (!response.ok) throw new Error("Failed");
      toast.success("Trade enregistre!");
      setTradeDialogOpen(false);
      navigate('/history');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du trade");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPaperTrade = async () => {
    if (!paperPositionSize || parseFloat(paperPositionSize) <= 0) {
      toast.error("Entrez une taille de position valide");
      return;
    }
    setSubmittingPaper(true);
    try {
      const response = await fetch(`${API}/paper/trade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: opportunity.symbol,
          market_type: opportunity.market_type || 'crypto',
          entry_price: opportunity.current_price,
          position_value: parseFloat(paperPositionSize),
          stop_loss: opportunity.stop_loss,
          take_profit: opportunity.take_profit?.[0] || opportunity.current_price * 1.05,
          score_at_entry: opportunity.score,
          notes: `Paper trade - Score ${opportunity.score}${paperTrailingEnabled ? ` - Trailing ${paperTrailingPercent}%` : ''}`,
          trailing_stop_percent: paperTrailingEnabled ? parseFloat(paperTrailingPercent) : null
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Erreur");
      toast.success(`Paper trade ouvert! Cash restant: $${data.cash_remaining?.toLocaleString()}`);
      setPaperTradeDialogOpen(false);
      navigate('/paper');
    } catch (error) {
      toast.error(error.message || "Erreur lors du paper trade");
    } finally {
      setSubmittingPaper(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="detail-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8"><Skeleton className="h-96" /></div>
          <div className="lg:col-span-4 space-y-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Opportunite non trouvee</p>
        <Button variant="outline" onClick={() => navigate('/')} className="mt-4 rounded-sm">Retour</Button>
      </div>
    );
  }

  const { current_price, price_change_24h, score, trend, opportunity_type, timing, risk_reward, entry_zone, stop_loss, take_profit, ai_analysis, indicators, score_criteria, price_history } = opportunity;

  const typeLabels = { breakout: "Breakout", reversal: "Reversal", trend_continuation: "Continuation" };
  const timingLabels = { enter_now: "ENTRER MAINTENANT", wait_confirmation: "ATTENDRE CONFIRMATION", too_late: "TROP TARD" };
  const timingColors = { enter_now: "bg-emerald-100 text-emerald-700", wait_confirmation: "bg-amber-100 text-amber-700", too_late: "bg-red-100 text-red-700" };

  return (
    <div data-testid="opportunity-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="rounded-sm" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{symbol}</h1>
            <Badge variant="outline" className="font-data">{typeLabels[opportunity_type] || opportunity_type}</Badge>
            <Badge className={timingColors[timing]}>{timingLabels[timing]}</Badge>
          </div>
          <p className="text-sm text-slate-500 font-body mt-1">{opportunity.name}</p>
        </div>
        <ScoreRing score={score} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-8 space-y-4">
          {/* Price & Chart */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div>
                <p className="font-data text-3xl font-bold text-slate-900 tabular-nums">
                  ${current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                </p>
                <p className={`font-data text-sm tabular-nums ${price_change_24h >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {price_change_24h >= 0 ? '+' : ''}{price_change_24h?.toFixed(2)}% (24h)
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant={chartMode === 'line' ? 'default' : 'outline'} size="sm" className="rounded-sm text-xs" onClick={() => setChartMode('line')} data-testid="chart-line-btn">
                  Ligne
                </Button>
                <Button variant={chartMode === 'candle' ? 'default' : 'outline'} size="sm" className="rounded-sm text-xs" onClick={() => setChartMode('candle')} data-testid="chart-candle-btn">
                  Chandeliers
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {chartMode === 'line' ? (
                <PriceChart data={price_history} support={indicators?.support} resistance={indicators?.resistance} />
              ) : (
                <CandlestickChartComponent symbol={symbol} />
              )}
            </CardContent>
          </Card>

          {/* AI Analysis */}
          {ai_analysis && (
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Lightning size={18} className="text-amber-500" />
                  <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Analyse IA</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none text-slate-700 font-body leading-relaxed whitespace-pre-line" data-testid="ai-analysis">
                  {ai_analysis}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Technical Indicators */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Indicateurs Techniques</CardTitle>
            </CardHeader>
            <CardContent>
              <TechnicalIndicators indicators={indicators} />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-4">
          {/* Score Breakdown */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Decomposition du Score</CardTitle>
            </CardHeader>
            <CardContent>
              <ScoreCriteria criteria={score_criteria} />
            </CardContent>
          </Card>

          {/* Trade Levels */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Niveaux de Trade</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-sm">
                <div className="flex items-center gap-2"><Target size={16} className="text-emerald-600" /><span className="text-sm font-body text-slate-600">Zone d'entree</span></div>
                <span className="font-data text-sm tabular-nums text-emerald-700">${entry_zone?.min?.toFixed(2)} - ${entry_zone?.max?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-red-50 rounded-sm">
                <div className="flex items-center gap-2"><ShieldWarning size={16} className="text-red-600" /><span className="text-sm font-body text-slate-600">Stop-Loss</span></div>
                <span className="font-data text-sm tabular-nums text-red-700">${stop_loss?.toFixed(2)}</span>
              </div>
              {take_profit?.map((tp, i) => (
                <div key={i} className="flex justify-between items-center p-2 bg-blue-50 rounded-sm">
                  <div className="flex items-center gap-2"><TrendUp size={16} className="text-blue-600" /><span className="text-sm font-body text-slate-600">TP {i + 1}</span></div>
                  <span className="font-data text-sm tabular-nums text-blue-700">${tp?.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center p-2 bg-slate-50 rounded-sm">
                <span className="text-sm font-body text-slate-600">Ratio R:R</span>
                <span className="font-data text-sm font-medium tabular-nums">{risk_reward}x</span>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Button 
            className={`w-full rounded-sm h-12 ${
              trend === 'bullish' ? 'bg-emerald-600 hover:bg-emerald-700' : 
              trend === 'bearish' ? 'bg-red-600 hover:bg-red-700' : ''
            }`} 
            onClick={handleTradeClick} 
            disabled={timing === 'too_late'} 
            data-testid="enter-trade-btn"
          >
            {trend === 'bullish' ? <TrendUp size={18} className="mr-2" weight="bold" /> : 
             trend === 'bearish' ? <TrendDown size={18} className="mr-2" weight="bold" /> :
             <Lightning size={18} className="mr-2" weight="fill" />}
            {timing === 'too_late' ? 'Trop tard pour entrer' : 
             trend === 'bullish' ? 'Acheter (Long)' : 
             trend === 'bearish' ? 'Vendre (Short)' : 'Entrer en Position'}
          </Button>
          <Button variant="outline" className="w-full rounded-sm h-10 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setPaperTradeDialogOpen(true)} disabled={timing === 'too_late'} data-testid="paper-trade-btn">
            <CurrencyCircleDollar size={18} className="mr-2" />
            Paper Trade (Virtuel)
          </Button>
        </div>
      </div>

      {/* Trade Dialog */}
      <Dialog open={tradeDialogOpen} onOpenChange={setTradeDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Entrer en Position - {symbol}</DialogTitle>
            <DialogDescription className="font-body">Configurez votre trade</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-slate-500">Prix d'entree</p><p className="font-data font-medium">${current_price?.toLocaleString()}</p></div>
              <div><p className="text-slate-500">Stop-Loss</p><p className="font-data font-medium text-red-600">${stop_loss?.toFixed(2)}</p></div>
              <div><p className="text-slate-500">Take-Profit</p><p className="font-data font-medium text-emerald-600">${take_profit?.[0]?.toFixed(2)}</p></div>
              <div><p className="text-slate-500">Score</p><p className="font-data font-medium">{score}</p></div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Taille de Position (USD)</Label>
              <Input type="number" step="100" placeholder="1000" value={positionSize} onChange={(e) => setPositionSize(e.target.value)} className="font-data rounded-sm" data-testid="position-size-input" />
            </div>
            {/* Trailing Stop */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
              <div>
                <p className="text-sm font-body font-medium">Trailing Stop-Loss</p>
                <p className="text-xs text-slate-400">Le SL suit le prix a la hausse</p>
              </div>
              <div className="flex items-center gap-2">
                {trailingStopEnabled && (
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.5" min="0.5" max="50" value={trailingStopPercent} onChange={(e) => setTrailingStopPercent(e.target.value)} className="font-data rounded-sm w-16 h-7 text-xs" data-testid="trailing-stop-input" />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                )}
                <Button variant={trailingStopEnabled ? "default" : "outline"} size="sm" className="rounded-sm text-xs h-7" onClick={() => setTrailingStopEnabled(!trailingStopEnabled)} data-testid="trailing-stop-toggle">
                  {trailingStopEnabled ? "ON" : "OFF"}
                </Button>
              </div>
            </div>
            {positionSize && (
              <div className="p-3 bg-slate-50 rounded-sm text-sm">
                <p className="text-slate-500">Risque: <span className="font-data text-red-600">${((current_price - stop_loss) * parseFloat(positionSize)).toFixed(2)}</span></p>
                <p className="text-slate-500">Objectif: <span className="font-data text-emerald-600">${((take_profit?.[0] - current_price) * parseFloat(positionSize)).toFixed(2)}</span></p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTradeDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleSubmitTrade} disabled={submitting} className="rounded-sm" data-testid="confirm-trade-btn">
              {submitting ? "Enregistrement..." : "Confirmer le Trade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-600">Position existante</DialogTitle>
            <DialogDescription className="font-body">
              Vous avez deja une position ouverte sur {symbol}. Voulez-vous quand meme ouvrir un nouveau trade?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={() => { setDuplicateDialogOpen(false); setTradeDialogOpen(true); }} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="confirm-duplicate-btn">
              Ouvrir quand meme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paper Trade Dialog */}
      <Dialog open={paperTradeDialogOpen} onOpenChange={setPaperTradeDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Paper Trade - {symbol}</DialogTitle>
            <DialogDescription className="font-body">Trade virtuel (portefeuille de simulation)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm">
              <p className="text-xs text-amber-700 font-body">Mode simulation — aucun argent reel n'est utilise</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-slate-500">Prix d'entree</p><p className="font-data font-medium">${current_price?.toLocaleString()}</p></div>
              <div><p className="text-slate-500">Stop-Loss</p><p className="font-data font-medium text-red-600">${stop_loss?.toFixed(2)}</p></div>
              <div><p className="text-slate-500">Take-Profit</p><p className="font-data font-medium text-emerald-600">${take_profit?.[0]?.toFixed(2)}</p></div>
              <div><p className="text-slate-500">Score</p><p className="font-data font-medium">{score}</p></div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Taille de Position (USD)</Label>
              <Input type="number" step="100" placeholder="5000" value={paperPositionSize} onChange={(e) => setPaperPositionSize(e.target.value)} className="font-data rounded-sm" data-testid="paper-position-size-input" />
            </div>
            {/* Trailing Stop for Paper */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-sm">
              <div>
                <p className="text-sm font-body font-medium">Trailing Stop-Loss</p>
                <p className="text-xs text-slate-400">Le SL suit le prix a la hausse</p>
              </div>
              <div className="flex items-center gap-2">
                {paperTrailingEnabled && (
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.5" min="0.5" max="50" value={paperTrailingPercent} onChange={(e) => setPaperTrailingPercent(e.target.value)} className="font-data rounded-sm w-16 h-7 text-xs" data-testid="paper-trailing-stop-input" />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                )}
                <Button variant={paperTrailingEnabled ? "default" : "outline"} size="sm" className="rounded-sm text-xs h-7" onClick={() => setPaperTrailingEnabled(!paperTrailingEnabled)} data-testid="paper-trailing-stop-toggle">
                  {paperTrailingEnabled ? "ON" : "OFF"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaperTradeDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleSubmitPaperTrade} disabled={submittingPaper} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="confirm-paper-trade-btn">
              {submittingPaper ? "Execution..." : "Ouvrir Paper Trade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
